import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'react'

import type { Invite, RsvpStatus } from '@/api/types'
import { shortDate } from '@/lib/dates'

/**
 * The invite itself: what it is, who is asking, and one control to answer.
 *
 * **One component, used by both doors.** `/r/:token` renders it for somebody
 * who arrived from an email with no account; the campaign screen renders it
 * for somebody signed in who followed a forwarded link. The frame around it
 * differs — one has the app shell, one does not — and the invite does not, so
 * the two cannot drift into showing different things. That is the same rule
 * `action` enforces for the buttons, applied to the content.
 *
 * Order is top to bottom because that is how an invite is read on a phone:
 * what it is, when, who is asking, then the answer.
 */

const GUEST_CHOICES = [0, 1, 2, 3, 4]

export function InviteCard({
  invite,
  onRespond,
  onWithdraw,
  busy = false,
  heading = true,
}: {
  invite: Invite
  onRespond: (status: RsvpStatus, guestCount: number) => void
  onWithdraw?: () => void
  busy?: boolean
  /**
   * Off where the screen around it already names the occasion.
   *
   * The campaign screen has the title, the organiser and the reply-by date
   * in its own header, and repeating them here read as the page having been
   * pasted twice. What is shared between the two doors is the ANSWER — the
   * part that must not drift — not the frame around it.
   */
  heading?: boolean
}) {
  /**
   * The party size is asked for only after "coming" is chosen.
   *
   * It starts from whatever they last said, so re-opening the page shows
   * their own answer rather than resetting them to "just me" — changing an
   * answer must never look like losing it.
   */
  const [guests, setGuests] = useState(invite.guest_count ?? 0)
  const [more, setMore] = useState((invite.guest_count ?? 0) > 4)

  const going = invite.rsvp_status === 'going'
  const answered = invite.rsvp_status != null

  return (
    <Box>
      {/* The image, first and full-bleed, because it is what tells somebody
          in half a second whether this is their kid's football fixture or a
          wedding. A campaign with no picture simply starts at the title —
          no placeholder, no grey box, no broken frame. */}
      {invite.hero_url && (
        <Box
          component="img"
          src={invite.hero_url}
          alt=""
          sx={{
            display: 'block',
            // Full-bleed sideways, and NOT upwards. The negative top margin
            // dragged the picture over the "Fronted" header on the public
            // page, where this card is not the first thing in the frame —
            // bleeding horizontally is what makes it look like an invite,
            // and bleeding vertically just covered something.
            width: 'calc(100% + 48px)',
            mx: '-24px',
            mt: 1.5,
            mb: 2.5,
            aspectRatio: '1200 / 630',
            objectFit: 'cover',
          }}
        />
      )}
      {heading && (
        <>
          <Typography variant="h2" sx={{ mb: 0.5 }}>
            {invite.label}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {invite.organiser} is asking
            {invite.rsvp_closes_at
              ? ` · reply by ${shortDate(invite.rsvp_closes_at)}`
              : ''}
          </Typography>

          {/* The latest update, above the note and marked as the newer thing.
              Somebody opening this from a message needs "we moved to the
              north gate" before "Saturday from 2pm by the bandstand" — the
              note is background and this is what changed. Plain text as it
              was typed, like everything else here. */}
          {invite.latest_update && (
            <Typography
              variant="body2"
              sx={{
                mt: 2,
                whiteSpace: 'pre-wrap',
                fontWeight: 620,
                color: 'text.primary',
              }}
            >
              {invite.latest_update}
            </Typography>
          )}

          {/* The organiser's note, plain text as it was typed. Nothing here
              is ever stored as markup, so there is no markup to trust. */}
          {invite.description && (
            <Typography
              variant="body2"
              sx={{ mt: 2, whiteSpace: 'pre-wrap', color: 'text.primary' }}
            >
              {invite.description}
            </Typography>
          )}

          <Divider sx={{ my: 2.5 }} />
        </>
      )}

      {/* Without the heading these are three naked buttons above a title
          that belongs to something else. One line says whose question it is. */}
      {!heading && (
        <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
          {/* Who is asking, as well as what is being asked.
            *
            * The heading carries both and is off here because the label is
            * already the page's title — but the organiser is not anywhere
            * else on that screen, so suppressing the heading lost the one
            * thing an invite cannot do without. A question from nobody is
            * indistinguishable from a form. */}
          {invite.organiser
            ? `${invite.organiser} is asking — are you coming?`
            : 'Are you coming?'}
        </Typography>
      )}

      {invite.closed ? (
        <Alert severity="info" sx={{ fontSize: '0.8125rem' }}>
          {answered
            ? `This has closed. You said ${label(invite.rsvp_status)}.`
            : 'This has closed — the organiser is no longer taking answers.'}
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {/* Equal weight, deliberately. A no is a useful answer and the
              product must not make it feel like failure — an invite that
              nudges you toward yes gets fewer answers of any kind.

              Tapping the answer you are already on takes it back, which is
              the same gesture the scoring page uses to take somebody off a
              side. It replaces a "Take my answer back" button that sat under
              these three competing with them: an undo is not a fourth
              answer, and a control that only ever appears after you have
              answered is a control you have to discover twice. */}
          <Stack direction="row" spacing={1}>
            <Button
              fullWidth
              size="large"
              variant={going ? 'contained' : 'outlined'}
              disabled={busy}
              onClick={() => (going && onWithdraw ? onWithdraw() : onRespond('going', guests))}
            >
              I&apos;m coming
            </Button>
            <Button
              fullWidth
              size="large"
              variant={invite.rsvp_status === 'not_going' ? 'contained' : 'outlined'}
              disabled={busy}
              onClick={() =>
                invite.rsvp_status === 'not_going' && onWithdraw
                  ? onWithdraw()
                  : onRespond('not_going', 0)
              }
            >
              Can&apos;t make it
            </Button>
          </Stack>

          <Button
            size="small"
            variant={invite.rsvp_status === 'maybe' ? 'contained' : 'text'}
            disabled={busy}
            onClick={() =>
              invite.rsvp_status === 'maybe' && onWithdraw
                ? onWithdraw()
                : onRespond('maybe', 0)
            }
            sx={{ alignSelf: 'flex-start' }}
          >
            Maybe
          </Button>

          {/* Only once "coming" is chosen, and discrete buttons rather than a
              stepper: nearly every real answer is 0 or 1 extra, and those
              should be one tap. `More` opens a field for the long tail. */}
          {going && (
            <Box>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.75 }}>
                Anyone with you?
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {GUEST_CHOICES.map((n) => (
                  <Chip
                    key={n}
                    label={n === 0 ? 'Just me' : `+${n}`}
                    color={!more && guests === n ? 'primary' : 'default'}
                    variant={!more && guests === n ? 'filled' : 'outlined'}
                    onClick={() => {
                      setMore(false)
                      setGuests(n)
                      onRespond('going', n)
                    }}
                  />
                ))}
                <Chip
                  label="More"
                  color={more ? 'primary' : 'default'}
                  variant={more ? 'filled' : 'outlined'}
                  onClick={() => setMore(true)}
                />
              </Stack>
              {more && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                  <input
                    type="number"
                    min={0}
                    max={20}
                    value={guests}
                    aria-label="How many others"
                    onChange={(e) => setGuests(Math.max(0, Number(e.target.value)))}
                    style={{ width: 72, padding: '6px 8px' }}
                  />
                  <Button size="small" onClick={() => onRespond('going', guests)}>
                    Save
                  </Button>
                </Stack>
              )}
            </Box>
          )}

          {/* The gesture, said once. A sentence rather than a button, because
              an undo competing with the three answers is a fourth thing to
              read every time somebody opens this. */}
          {answered && onWithdraw && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Tap your answer again to take it back.
            </Typography>
          )}
        </Stack>
      )}

      {/* Social proof, and only when the organiser left it on. Counts of
          people, never a list — the roster belongs to them. */}
      {invite.going != null && invite.asked != null && (
        <Typography variant="caption" sx={{ display: 'block', mt: 2.5 }}>
          {invite.going === 0
            ? // "Nobody has answered" was wrong the moment somebody said no,
              // which is a real answer and one the organiser wanted. The
              // count is of people COMING, so the sentence says that.
              `Nobody has said yes yet — ${invite.asked} were asked.`
            : `${invite.going} of ${invite.asked} are coming` +
              (invite.heads && invite.heads > invite.going
                ? ` · ${invite.heads} people in all`
                : '')}
        </Typography>
      )}
    </Box>
  )
}

function label(status: Invite['rsvp_status']): string {
  if (status === 'going') return 'you were coming'
  if (status === 'not_going') return 'you could not make it'
  return 'maybe'
}
